import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Psychology as AIIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  skills?: SkillSuggestion[];
}

interface SkillSuggestion {
  id: string;
  name: string;
  domain: string;
  relevance: string;
}

interface AIAssistDialogProps {
  open: boolean;
  onClose: () => void;
  onResults: (skillIds: string[]) => void;
}

const AIAssistDialog: React.FC<AIAssistDialogProps> = ({ open, onClose, onResults }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      // Call the AI to interpret the request and find relevant skills
      const response = await fetch('http://localhost:8081/v1/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `{
            Get {
              CHISGElement(
                nearText: { concepts: ["${userMessage}"] }
                limit: 15
              ) {
                name
                domain
                layer
                description
                _additional { id distance }
              }
            }
          }`
        }),
      });

      const data = await response.json();
      const elements = data?.data?.Get?.CHISGElement || [];

      // Transform to skill suggestions
      const skills: SkillSuggestion[] = elements.map((el: any) => ({
        id: el._additional.id,
        name: el.name,
        domain: el.domain,
        relevance: el._additional.distance < 0.3 ? 'High' : 
                   el._additional.distance < 0.5 ? 'Medium' : 'Low',
      }));

      // Generate AI response
      const skillNames = skills.slice(0, 5).map(s => s.name).join(', ');
      const aiResponse = skills.length > 0
        ? `Based on your query, I found ${skills.length} relevant skills. The most relevant are: **${skillNames}**.\n\nYou can select the skills you want to highlight on the map.`
        : `I couldn't find skills directly matching your query. Try rephrasing or being more specific about the competency area you're interested in.`;

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse,
        skills: skills.slice(0, 10),
      }]);

    } catch (error) {
      console.error('AI query failed:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error processing your request. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleSkillSelection = (skillId: string) => {
    setSelectedSkills(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleApplyFilter = () => {
    onResults(selectedSkills);
    handleClose();
  };

  const handleClose = () => {
    setInput('');
    setMessages([]);
    setSelectedSkills([]);
    onClose();
  };

  const getDomainColor = (domain: string): string => {
    const colors: Record<string, string> = {
      'COGNITION': '#4CAF50',
      'PROBLEM SOLVING': '#2196F3',
      'PEOPLE': '#E91E63',
      'SELF': '#9C27B0',
      'COMMUNICATION': '#FF9800',
      'LANGUAGE': '#00BCD4',
      'LITERACY': '#795548',
      'STRATEGIC': '#607D8B',
      'FOCUS & TOOLS': '#3F51B5',
      'NUMBERS': '#F44336',
      'REASONING': '#009688',
    };
    return colors[domain] || '#666';
  };

  // Example prompts
  const examplePrompts = [
    "What skills help with public speaking?",
    "Show me skills for data analysis",
    "Find leadership development skills",
    "Skills for creative problem solving",
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2D2D3A',
          color: '#fff',
          height: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: '#009688' }} />
          <Typography variant="h6">AI Skill Assistant</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#888' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {/* Messages Area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SparkleIcon sx={{ fontSize: 48, color: '#009688', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Ask me about skills
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                I can help you find relevant skills based on your learning goals, 
                career interests, or specific competencies you want to develop.
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {examplePrompts.map((prompt, i) => (
                  <Chip
                    key={i}
                    label={prompt}
                    onClick={() => setInput(prompt)}
                    sx={{
                      backgroundColor: '#3D3D4A',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#4D4D5A' },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  maxWidth: '80%',
                  backgroundColor: msg.role === 'user' ? '#009688' : '#3D3D4A',
                  color: '#fff',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
                
                {/* Skill suggestions in message */}
                {msg.skills && msg.skills.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ borderColor: '#555', my: 1 }} />
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      Click to select:
                    </Typography>
                    <List dense sx={{ mt: 0.5 }}>
                      {msg.skills.map((skill) => (
                        <ListItem key={skill.id} disablePadding>
                          <ListItemButton
                            selected={selectedSkills.includes(skill.id)}
                            onClick={() => toggleSkillSelection(skill.id)}
                            sx={{
                              borderRadius: 1,
                              py: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                              },
                            }}
                          >
                            <Chip
                              size="small"
                              label={skill.domain}
                              sx={{
                                backgroundColor: getDomainColor(skill.domain),
                                color: '#fff',
                                fontSize: '0.6rem',
                                height: 18,
                                mr: 1,
                              }}
                            />
                            <ListItemText
                              primary={skill.name}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { fontSize: '0.8rem' }
                              }}
                            />
                            <Chip
                              size="small"
                              label={skill.relevance}
                              variant="outlined"
                              sx={{
                                borderColor: skill.relevance === 'High' ? '#4CAF50' :
                                           skill.relevance === 'Medium' ? '#FF9800' : '#666',
                                color: skill.relevance === 'High' ? '#4CAF50' :
                                       skill.relevance === 'Medium' ? '#FF9800' : '#666',
                                fontSize: '0.6rem',
                                height: 18,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>
            </Box>
          ))}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper sx={{ p: 2, backgroundColor: '#3D3D4A', borderRadius: 2 }}>
                <CircularProgress size={20} sx={{ color: '#009688' }} />
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: '1px solid #444' }}>
          {selectedSkills.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {selectedSkills.length} skill(s) selected
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Ask about skills..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              variant="outlined"
              InputProps={{
                sx: {
                  backgroundColor: '#1D1D29',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#444',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#666',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#009688',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading}
              sx={{
                backgroundColor: '#009688',
                color: '#fff',
                '&:hover': { backgroundColor: '#00796B' },
                '&.Mui-disabled': { backgroundColor: '#444', color: '#666' },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #444' }}>
        <Button onClick={handleClose} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          disabled={selectedSkills.length === 0}
          startIcon={<SparkleIcon />}
          sx={{
            backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
          }}
        >
          Show on Map ({selectedSkills.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIAssistDialog;
